import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Plus, Search, Filter, Loader2, FileText } from 'lucide-react';
import { faelleAPI } from '../utils/api';
import { useToast } from '../hooks/use-toast';

const Gutachten = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [faelle, setFaelle] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFaelle = async () => {
    try {
      setLoading(true);
      const data = await faelleAPI.getAll();
      setFaelle(data);
    } catch (err) {
      console.error('Failed to fetch fälle:', err);
      if (err.response && err.response.status === 401) {
        localStorage.removeItem('autogutachter24_user');
        localStorage.removeItem('autogutachter24_token');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaelle();
  }, []);

  const getStatusColor = (status) => {
    const colors = {
      neu: 'bg-blue-100 text-blue-700',
      in_bearbeitung: 'bg-purple-100 text-purple-700',
      abgeschlossen: 'bg-green-100 text-green-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const filteredFaelle = faelle.filter(f =>
    f.aktenzeichen.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.kennzeichen.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gutachten</h1>
            <p className="text-gray-500 mt-1">Verwalten Sie Ihre Fahrzeuggutachten</p>
          </div>
          <Button 
            className="bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all"
            onClick={() => navigate('/faelle/neu')}
          >
            <Plus className="w-4 h-4 mr-2" />
            Neues Gutachten
          </Button>
        </div>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Suche nach Aktenzeichen oder Kennzeichen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-12 bg-gray-50 border-0 focus:bg-white"
                />
              </div>
              <Button variant="outline" className="h-12">
                <Filter className="w-4 h-4 mr-2" />
                Filter
              </Button>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : faelle.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Noch keine Fälle</h3>
              <p className="text-gray-500 mb-6">Erstellen Sie Ihren ersten Fall</p>
              <Button 
                className="bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white"
                onClick={() => navigate('/faelle/neu')}
              >
                <Plus className="w-4 h-4 mr-2" />
                Ersten Fall erstellen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredFaelle.map((fall) => (
              <Card 
                key={fall.fall_id} 
                className="border-0 shadow-lg hover:shadow-xl transition-all cursor-pointer"
                onClick={() => navigate(`/fall/${fall.fall_id}`)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-bold text-gray-900">{fall.aktenzeichen}</h3>
                        <Badge className={getStatusColor(fall.status)}>
                          {fall.status}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Kennzeichen:</span>
                          <span className="ml-2 font-semibold text-gray-900">{fall.kennzeichen}</span>
                        </div>
                        {fall.versicherer && (
                          <div>
                            <span className="text-gray-500">Versicherer:</span>
                            <span className="ml-2 text-gray-900">{fall.versicherer}</span>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-500">Erstellt:</span>
                          <span className="ml-2 text-gray-900">
                            {new Date(fall.created_at).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button variant="outline">Details</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Gutachten;